import React, { Suspense, lazy } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchConsentCoverage,
  type ConsentCoveragePoint,
} from '../../diagnostics/analytics.api.js';

const CoverageChart = lazy(() => import('./CoverageChart.js'));

export function CoverageCard({
  baseUrl,
  siteKey,
  from,
  to,
  comparePrev,
}: {
  baseUrl: string;
  siteKey: string;
  from: string;
  to: string;
  comparePrev: boolean;
}) {
  const covQ = useQuery({
    queryKey: ['cmp', 'coverage', baseUrl, siteKey, from, to],
    queryFn: () => fetchConsentCoverage(baseUrl, siteKey, `${from}T00:00:00Z`, `${to}T23:59:59Z`),
    enabled: Boolean(baseUrl),
    staleTime: 60_000,
  });

  const prevRange = React.useMemo(() => {
    try {
      const fromDate = new Date(`${from}T00:00:00Z`);
      const toDate = new Date(`${to}T23:59:59Z`);
      const spanMs = toDate.getTime() - fromDate.getTime() + 1;
      const prevTo = new Date(fromDate.getTime() - 1);
      const prevFrom = new Date(prevTo.getTime() - spanMs + 1);
      return { prevFromISO: prevFrom.toISOString(), prevToISO: prevTo.toISOString() };
    } catch {
      return { prevFromISO: undefined, prevToISO: undefined } as const;
    }
  }, [from, to]);

  const covPrevQ = useQuery({
    queryKey: [
      'cmp',
      'coverage',
      'prev',
      baseUrl,
      siteKey,
      prevRange.prevFromISO ?? null,
      prevRange.prevToISO ?? null,
    ],
    queryFn: () =>
      fetchConsentCoverage(
        baseUrl,
        siteKey,
        prevRange.prevFromISO as string,
        prevRange.prevToISO as string,
      ),
    enabled: Boolean(baseUrl && comparePrev && prevRange.prevFromISO && prevRange.prevToISO),
    staleTime: 60_000,
  });

  if (!covQ.data || covQ.data.length === 0) return null;

  const sum = (arr: ConsentCoveragePoint[], k: keyof ConsentCoveragePoint) =>
    arr.reduce((a, p) => a + (Number(p[k]) || 0), 0);
  const tot = sum(covQ.data as ConsentCoveragePoint[], 'total');
  const tc = sum(covQ.data as ConsentCoveragePoint[], 'tc');
  const gpp = sum(covQ.data as ConsentCoveragePoint[], 'gpp');
  const tcPct = tot > 0 ? (tc / tot) * 100 : 0;
  const gppPct = tot > 0 ? (gpp / tot) * 100 : 0;
  let tcDelta: number | null = null;
  let gppDelta: number | null = null;
  if (comparePrev && covPrevQ.data && covPrevQ.data.length) {
    const prev = covPrevQ.data as ConsentCoveragePoint[];
    const ptot = sum(prev, 'total');
    const ptc = sum(prev, 'tc');
    const pgpp = sum(prev, 'gpp');
    const ptcPct = ptot > 0 ? (ptc / ptot) * 100 : 0;
    const pgppPct = ptot > 0 ? (pgpp / ptot) * 100 : 0;
    tcDelta = tcPct - ptcPct;
    gppDelta = gppPct - pgppPct;
  }

  return (
    <div className="rounded-lg border p-4 text-sm">
      <h2 className="text-lg font-semibold mb-2">Consent Coverage</h2>
      <div className="mb-2 text-xs text-gray-600 flex gap-4">
        <span>
          TC coverage: {tcPct.toFixed(1)}%
          {tcDelta !== null && (
            <span className={tcDelta >= 0 ? 'text-green-600' : 'text-red-600'}>
              {' '}
              ({tcDelta >= 0 ? '↑' : '↓'} {Math.abs(tcDelta).toFixed(1)}%)
            </span>
          )}
        </span>
        <span>
          GPP coverage: {gppPct.toFixed(1)}%
          {gppDelta !== null && (
            <span className={gppDelta >= 0 ? 'text-green-600' : 'text-red-600'}>
              {' '}
              ({gppDelta >= 0 ? '↑' : '↓'} {Math.abs(gppDelta).toFixed(1)}%)
            </span>
          )}
        </span>
      </div>
      <div className="h-56">
        <Suspense fallback={<div className="text-xs text-gray-500">Loading chart…</div>}>
          <CoverageChart data={covQ.data} />
        </Suspense>
      </div>
      <div className="mt-2 flex gap-2 text-xs">
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            try {
              const u = new URL('/api/admin/analytics/consent', baseUrl);
              u.searchParams.set('siteKey', siteKey);
              u.searchParams.set('from', `${from}T00:00:00Z`);
              u.searchParams.set('to', `${to}T23:59:59Z`);
              void navigator.clipboard?.writeText(u.toString());
            } catch {
              /* noop */
            }
          }}
        >
          Copy API
        </button>
        <button
          className="px-2 py-1 border rounded"
          onClick={() => {
            const rows = covQ.data as ConsentCoveragePoint[];
            const header = 'bucket,total,tc,gpp\n';
            const body = rows.map((r) => `${r.bucket},${r.total},${r.tc},${r.gpp}`).join('\n');
            const blob = new Blob([header + body], { type: 'text/csv' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `coverage-${siteKey}-${from}-${to}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          }}
        >
          Download CSV
        </button>
      </div>
    </div>
  );
}

export default CoverageCard;
